// editor.js
import { polyBounds } from "./geometry.js";

function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

function rectToPoly(x0, y0, x1, y1) {
  const minx = Math.min(x0, x1), maxx = Math.max(x0, x1);
  const miny = Math.min(y0, y1), maxy = Math.max(y0, y1);
  return [minx, miny, maxx, miny, maxx, maxy, minx, maxy];
}

// ellipse approximated by N segments
function ellipseToPoly(cx, cy, rx, ry, n = 32) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    out.push(cx + Math.cos(t) * rx, cy + Math.sin(t) * ry);
  }
  return out;
}

export function createOverlayEditor(deps) {
  const {
    app,
    overlaysM,
    view,
    applyTransform,
    scheduleTilesRedraw,
    BASE_W,
    BASE_H,
  } = deps;

  // ----- state -----
  let selection = null; // { overlayId, polyIndex }
  let mode = "select";  // select | draw-poly | draw-rect | draw-ellipse | drag-vertex
  let drawPts = [];     // [x,y,x,y,...] for draw-poly
  let drag = null;      // { vi } vertex index (0..n-1)
  let startPt = null;   // for rect/ellipse drag

  // overlay for editor visuals (handles + preview)
  const editorLayer = new PIXI.Container();
  editorLayer.zIndex = 25000;

  // IMPORTANT: attach to WORLD space so it follows view.scale/view.tx/view.ty
  deps.world.addChild(editorLayer);
  // alternatively: deps.polyLayer.addChild(editorLayer);


  const gPreview = new PIXI.Graphics();
  const gHandles = new PIXI.Graphics();
  editorLayer.addChild(gPreview, gHandles);

  function screenToBase(sx, sy) {
    const inv = 1 / view.scale;
    return {
      x: (sx - view.tx) * inv,
      y: (sy - view.ty) * inv,
    };
  }

  function baseToScreen(bx, by) {
    return {
      x: bx * view.scale + view.tx,
      y: by * view.scale + view.ty,
    };
  }

  function drawHandles() {
    gHandles.clear();
    if (!selection) return;

    const ov = overlaysM.getOverlayById(selection.overlayId);
    const poly = ov?.polys?.[selection.polyIndex];
    if (!poly) return;

    // handle radius in screen px -> convert to base units
    const rScreen = 7;
    const s = Math.max(1e-6, deps.world.scale.x);
    const rBase = rScreen / s;
    
    gHandles.lineStyle(1, 0xffffff, 0.95);
    gHandles.beginFill(0x000000, 0.35);

    for (let i = 0; i < poly.length; i += 2) {
      gHandles.drawCircle(poly[i], poly[i + 1], rBase);
    }

    gHandles.endFill();
  }

  function drawPreviewPoly(poly) {
    gPreview.clear();
    if (!poly || poly.length < 6) return;
    gPreview.lineStyle(1, 0xffffff, 0.9);
    gPreview.beginFill(0x000000, 0.10);
    gPreview.drawPolygon(poly);
    gPreview.endFill();
  }

  function clearPreview() {
    gPreview.clear();
  }

  function pickVertex(bx, by) {
    if (!selection) return null;
    const ov = overlaysM.getOverlayById(selection.overlayId);
    const poly = ov?.polys?.[selection.polyIndex];
    if (!poly) return null;

    const rScreen = 10;
    const rBase = rScreen / Math.max(1e-6, view.scale);
    const r2 = rBase * rBase;

    let best = null;
    for (let vi = 0; vi < poly.length; vi += 2) {
      const dx = poly[vi] - bx;
      const dy = poly[vi + 1] - by;
      const d2 = dx * dx + dy * dy;
      if (d2 <= r2) { best = vi; break; }
    }
    return best;
  }

  function selectFromPick(picked) {
    if (!picked) { selection = null; drawHandles(); return; }
    selection = { overlayId: picked.overlayId, polyIndex: picked.polyIndex };
    drawHandles();
  }

  function ensureHasSelectionOverlay() {
    // To create shapes we need an overlayId to add into.
    // If none selected, pick the "first" overlay as a fallback.
    if (selection?.overlayId) return selection.overlayId;
    const first = overlaysM.overlays?.[0]?.id;
    if (first) {
      selection = { overlayId: first, polyIndex: 0 };
      return first;
    }
    return null;
  }
  let lastScale = null;

  function onViewChanged() {
    const s = deps.world.scale.x; // or view.scale if you prefer
    if (lastScale === s) return;
    lastScale = s;
    drawHandles(); // recomputes rBase using current scale
  }


  // ----- keyboard -----
  function onKeyDown(e) {
    // creation modes
    if (e.key === "p" || e.key === "P") { mode = "draw-poly"; drawPts = []; clearPreview(); return; }
    if (e.key === "r" || e.key === "R") { mode = "draw-rect"; drawPts = []; clearPreview(); return; }
    if (e.key === "e" || e.key === "E") { mode = "draw-ellipse"; drawPts = []; clearPreview(); return; }

    // cancel
    if (e.key === "Escape") {
      mode = "select";
      drawPts = [];
      startPt = null;
      drag = null;
      clearPreview();
      scheduleTilesRedraw();
      return;
    }

    // finish polygon
    if (mode === "draw-poly" && e.key === "Enter") {
      const overlayId = ensureHasSelectionOverlay();
      if (overlayId && drawPts.length >= 6) {
        overlaysM.addPoly(overlayId, drawPts.slice());
        // select the newly created poly
        const ov = overlaysM.getOverlayById(overlayId);
        selection = { overlayId, polyIndex: ov.polys.length - 1 };
      }
      mode = "select";
      drawPts = [];
      clearPreview();
      drawHandles();
      scheduleTilesRedraw();
      return;
    }

    // delete selected polygon
    if ((e.key === "Delete" || e.key === "Backspace") && selection) {
      const { overlayId, polyIndex } = selection;
      overlaysM.deletePoly(overlayId, polyIndex);
      selection = null;
      clearPreview();
      drawHandles();
      scheduleTilesRedraw();
    }
  }

  window.addEventListener("keydown", onKeyDown);

  // ----- pointer -----
  app.stage.eventMode = "static";
  app.stage.hitArea = app.screen;

  app.stage.on("pointerdown", (ev) => {
    ev.stopPropagation();  // ✅ critical
    const b = screenToBase(ev.global.x, ev.global.y);
    const bx = clamp(b.x, 0, BASE_W);
    const by = clamp(b.y, 0, BASE_H);

    if (mode === "select") {
      // if clicking a vertex handle, start dragging it
      const vi = pickVertex(bx, by);
      if (vi != null) {
        mode = "drag-vertex";
        drag = { vi };
        return;
      }

      // else pick polygon
      const picked = overlaysM.pickAtBasePoint(bx, by);
      selectFromPick(picked);
      scheduleTilesRedraw();
      return;
    }

    if (mode === "draw-poly") {
      drawPts.push(bx, by);
      drawPreviewPoly(drawPts);
      scheduleTilesRedraw();
      return;
    }

    if (mode === "draw-rect" || mode === "draw-ellipse") {
      startPt = { x: bx, y: by };
      return;
    }
  });

  app.stage.on("pointermove", (ev) => {
    ev.stopPropagation();  
    const b = screenToBase(ev.global.x, ev.global.y);
    const bx = clamp(b.x, 0, BASE_W);
    const by = clamp(b.y, 0, BASE_H);

    if (mode === "drag-vertex" && selection && drag) {
      const ov = overlaysM.getOverlayById(selection.overlayId);
      const poly = ov?.polys?.[selection.polyIndex];
      if (!poly) return;

      poly[drag.vi] = bx;
      poly[drag.vi + 1] = by;

      overlaysM.updatePoly(selection.overlayId, selection.polyIndex, poly);
      drawHandles();
      scheduleTilesRedraw();
      return;
    }

    if ((mode === "draw-rect" || mode === "draw-ellipse") && startPt) {
      if (mode === "draw-rect") {
        drawPreviewPoly(rectToPoly(startPt.x, startPt.y, bx, by));
      } else {
        const cx = (startPt.x + bx) / 2;
        const cy = (startPt.y + by) / 2;
        const rx = Math.abs(bx - startPt.x) / 2;
        const ry = Math.abs(by - startPt.y) / 2;
        drawPreviewPoly(ellipseToPoly(cx, cy, rx, ry, 40));
      }
      scheduleTilesRedraw();
      return;
    }
  });

  app.stage.on("pointerup", (ev) => {
    ev.stopPropagation();  
    const b = screenToBase(ev.global.x, ev.global.y);
    const bx = clamp(b.x, 0, BASE_W);
    const by = clamp(b.y, 0, BASE_H);

    if (mode === "drag-vertex") {
      mode = "select";
      drag = null;
      scheduleTilesRedraw();
      return;
    }

    if ((mode === "draw-rect" || mode === "draw-ellipse") && startPt) {
      const overlayId = ensureHasSelectionOverlay();
      if (overlayId) {
        let poly = null;
        if (mode === "draw-rect") {
          poly = rectToPoly(startPt.x, startPt.y, bx, by);
        } else {
          const cx = (startPt.x + bx) / 2;
          const cy = (startPt.y + by) / 2;
          const rx = Math.abs(bx - startPt.x) / 2;
          const ry = Math.abs(by - startPt.y) / 2;
          poly = ellipseToPoly(cx, cy, rx, ry, 40);
        }

        // ignore tiny shapes
        const bnd = polyBounds(poly);
        const w = bnd.maxx - bnd.minx;
        const h = bnd.maxy - bnd.miny;
        if (w > 2 && h > 2) {
          overlaysM.addPoly(overlayId, poly);
          const ov = overlaysM.getOverlayById(overlayId);
          selection = { overlayId, polyIndex: ov.polys.length - 1 };
        }
      }

      startPt = null;
      mode = "select";
      clearPreview();
      drawHandles();
      scheduleTilesRedraw();
      return;
    }
  });

  app.stage.on("pointerupoutside", () => {
    if (mode === "drag-vertex") { mode = "select"; drag = null; }
    startPt = null;
  });

  function destroy() {
    window.removeEventListener("keydown", onKeyDown);
    editorLayer.destroy({ children: true });
  }

  onViewChanged();

  function clearSelection() {
    selection = null;
    mode = "select";
    drawPts = [];
    startPt = null;
    drag = null;
    gPreview.clear();
    gHandles.clear();
  }
  
  
  function deleteSelectedPolygon() {
    if (!selection) return false;
    const { overlayId, polyIndex } = selection;
    const ok = overlaysM.deletePoly(overlayId, polyIndex);
    selection = null;
    gPreview.clear();
    gHandles.clear();
    scheduleTilesRedraw();
    return ok;
  }
  
  function setTool(next) {
    // next: "select" | "draw-poly" | "draw-rect" | "draw-ellipse"
    mode = next;
    drawPts = [];
    startPt = null;
    drag = null;
    gPreview.clear();
    drawHandles();
    scheduleTilesRedraw();
  }
  
  function selectOverlayAndPoly(overlayId, polyIndex = 0) {
    selection = { overlayId, polyIndex };
    drawHandles();
    scheduleTilesRedraw();
  }
  
  return {
    destroy,
    getSelection: () => selection,
    clearSelection,
    deleteSelectedPolygon,
    setTool,
    selectOverlayAndPoly,
  };
  
  
}
